import type { FC } from 'react';
import { CreditCard, Star, Trash2, Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import { getBrandDisplay } from '../../utils/cardBrands';
import type { SavedPaymentMethod } from '../../api/paymentMethodsApi';

interface PaymentCardProps {
  method: SavedPaymentMethod;
  onSetDefault: () => void;
  onRemove: () => void;
  isSettingDefault: boolean;
}

export const PaymentCard: FC<PaymentCardProps> = ({
  method,
  onSetDefault,
  onRemove,
  isSettingDefault,
}) => {
  const brandInfo = getBrandDisplay(method.brand);

  const expiryDate = `${String(method.expiryMonth).padStart(2, '0')}/${String(
    method.expiryYear
  ).slice(-2)}`;

  return (
    <div
      className={cn(
        'flex items-center justify-between p-4 bg-white border rounded-lg',
        method.isDefault ? 'border-primary-300 bg-primary-50/30' : 'border-gray-200'
      )}
    >
      <div className="flex items-center gap-4">
        <div className="p-2 bg-gray-100 rounded-lg">
          <CreditCard className={cn('w-6 h-6', brandInfo.color)} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className={cn('font-medium', brandInfo.color)}>{brandInfo.name}</span>
            <span className="text-gray-600">**** {method.last4}</span>
            {method.isDefault && (
              <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-primary-700 bg-primary-100 rounded-full">
                <Star className="w-3 h-3" />
                Default
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500">Expires {expiryDate}</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {!method.isDefault && (
          <button
            onClick={onSetDefault}
            disabled={isSettingDefault}
            className={cn(
              'px-3 py-1.5 text-sm font-medium text-gray-700',
              'bg-white border border-gray-300 rounded-lg',
              'hover:bg-gray-50 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isSettingDefault ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Set Default'
            )}
          </button>
        )}
        <button
          onClick={onRemove}
          className={cn(
            'p-2 text-gray-500 rounded-lg',
            'hover:bg-red-50 hover:text-red-600 transition-colors'
          )}
          aria-label="Remove card"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
